import React, { useRef } from "react";
import { FiSend, FiPaperclip, FiX, FiFile } from "react-icons/fi";

interface MessageInputProps {
  value: string;
  attachedFiles: File[];
  onValueChange: (value: string) => void;
  onSend: () => void;
  onFileSelect: (files: File[]) => void;
  onRemoveAttachment: (index: number) => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  value,
  attachedFiles,
  onValueChange,
  onSend,
  onFileSelect,
  onRemoveAttachment,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    onFileSelect(files);
    e.target.value = "";
  };

  return (
    <div className="conversations-input-area conversations-input-safe bg-white dark:bg-gray-800 px-3 py-3 border-t border-gray-200 dark:border-gray-700 md:px-4">
      {/* Attached Files Preview */}
      {attachedFiles.length > 0 && (
        <div className="mb-2.5 flex flex-wrap gap-2 md:mb-3">
          {attachedFiles.map((file, index) => {
            const isImage = file.type.startsWith("image/");
            return (
              <div
                key={index}
                className="relative group bg-gray-100 dark:bg-gray-700 rounded-lg p-2 flex items-center gap-2 max-w-full md:max-w-xs"
              >
                {isImage ? (
                  <>
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-11 h-11 object-cover rounded md:w-12 md:h-12"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                      {file.name}
                    </span>
                  </>
                ) : (
                  <>
                    <div className="w-11 h-11 bg-purple-100 dark:bg-purple-900 rounded flex items-center justify-center flex-shrink-0 md:w-12 md:h-12">
                      <FiFile
                        size={22}
                        className="text-purple-600 dark:text-purple-400 md:text-[24px]"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {Math.round(file.size / 1024)} KB
                      </p>
                    </div>
                  </>
                )}
                <button
                  onClick={() => onRemoveAttachment(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition active:scale-95 md:p-1"
                >
                  <FiX size={16} className="md:text-[14px]" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Input */}
      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition active:scale-95 md:p-2"
        >
          <FiPaperclip size={24} />
        </button>

        <div className="flex-1">
          <textarea
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite uma mensagem..."
            rows={1}
            className="w-full px-3 py-2.5 bg-gray-100 dark:bg-gray-700 rounded-lg resize-none outline-none focus:ring-2 focus:ring-purple-400 dark:text-white text-sm max-h-32 md:px-4 md:py-2"
            style={{ minHeight: "44px" }}
          />
        </div>

        <button
          onClick={onSend}
          disabled={!value.trim() && attachedFiles.length === 0}
          className="p-2.5 bg-purple-400 text-white rounded-full hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 md:p-2"
        >
          <FiSend size={22} className="md:text-[20px]" />
        </button>
      </div>

      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center md:text-[9px]">
        Pressione Enter para enviar • Shift+Enter para nova linha
      </p>
    </div>
  );
};
